'use client';

import { useEffect, useRef, useMemo } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import {
  useGetTerritoryQuery,
  useGetStrategyMapMembersQuery,
  useGetTargetStrategyHeatmapQuery,
  useGetMyChainedStrategiesQuery,
} from 'src/api/redux';
import { useDebounce } from 'use-debounce';
import { getTargetStrategyIdString } from 'src/utils/strategyHelpers';
import {
  convertTerritoryPolygonsToMapPaths,
  getPolygonBoundaryCoords,
} from 'src/utils/territory';

const StrategyMapContent = () => {
  const map = useMap();
  const polygonRef = useRef(null);
  const deckOverlayRef = useRef<GoogleMapsOverlay | null>(null);
  const {
    selectedTerritory,
    boundedTerritory,
    selectedChainIds,
    selectedSpecialties,
    selectedStatuses,
    selectedProviderLists,
    bounds,
    zoom,
    showHeatmap,
    selectedHeatmapMetric,
  } = useStrategies(LOCATION.STRATEGY_CENTER);

  // Use boundedTerritory for display if available, otherwise selectedTerritory
  const displayTerritory = boundedTerritory || selectedTerritory;

  const { data: territory } = useGetTerritoryQuery(
    {
      territoryId: displayTerritory?.id.toString(),
    },
    {
      skip: !displayTerritory?.id,
    }
  );

  const { data: chainedStrategies } = useGetMyChainedStrategiesQuery();
  const selectedChain = useMemo(() => {
    if (selectedChainIds.length !== 1) return null;
    return chainedStrategies?.results?.find(
      (s: any) => s.id === selectedChainIds[0]
    ) ?? null;
  }, [selectedChainIds, chainedStrategies?.results]);
  const strategyId = getTargetStrategyIdString(selectedChain);

  // Fetch map members for heatmap (without search/sort params to get all members)
  const { data: mapMembers } = useGetStrategyMapMembersQuery(
    {
      strategyId,
      territoryId: selectedTerritory?.id.toString(),
      bounding_territory: boundedTerritory?.id.toString(),
      specialties: selectedSpecialties,
      statuses: selectedStatuses,
      provider_lists: selectedProviderLists,
      ...bounds,
    },
    {
      skip: !showHeatmap || !strategyId || !selectedTerritory?.id,
    }
  );

  // Heatmap metric data - for heatmap layer with selectedHeatmapMetric
  const { data: hmdata, isFetching: isHeatmapFetching } =
    useGetTargetStrategyHeatmapQuery(
      {
        strategyId: strategyId || '',
        territoryId: selectedTerritory?.id?.toString() || '',
        params: {
          metric: showHeatmap ? selectedHeatmapMetric?.code : undefined,
        },
      },
      {
        skip:
          !strategyId ||
          !selectedTerritory?.id ||
          !showHeatmap ||
          !selectedHeatmapMetric,
      }
    );

  // Debounce zoom for smooth radius transitions
  const [debouncedZoom] = useDebounce(zoom, 300);

  // Create heatmap data - use hmdata.points with volume if metric selected, else use mapMembers with weight=1
  const heatmapData = useMemo(() => {
    // If a metric is selected, we MUST use hmdata - don't fall back to mapMembers
    if (selectedHeatmapMetric) {
      // Wait for hmdata to load before showing anything (also clear during fetching to avoid stale data)
      if (isHeatmapFetching || !hmdata?.points || !Array.isArray(hmdata.points)) {
        return [];
      }
      return hmdata.points
        .filter(
          (point: { lat?: number; lng?: number; volume?: number }) =>
            point.lat &&
            point.lng &&
            typeof point.lat === 'number' &&
            typeof point.lng === 'number'
        )
        .map((point: { lat: number; lng: number; volume: number }) => ({
          position: [point.lng, point.lat],
          weight: point.volume || 1,
        }));
    }

    // No metric selected - use mapMembers with weight=1 for provider count
    if (!mapMembers || !Array.isArray(mapMembers)) return [];

    return mapMembers
      .filter(
        (member: { lat?: number; lng?: number }) =>
          member.lat &&
          member.lng &&
          typeof member.lat === 'number' &&
          typeof member.lng === 'number'
      )
      .map((member: { lat: number; lng: number }) => ({
        position: [member.lng, member.lat],
        weight: 1, // Provider count - each provider counts as 1
      }));
  }, [mapMembers, selectedHeatmapMetric, hmdata, isHeatmapFetching]);

  // Initialize deck.gl overlay once when map is available
  useEffect(() => {
    if (!map) return;

    // Create new overlay instance
    const overlay = new GoogleMapsOverlay({ interleaved: false });
    overlay.setMap(map);
    deckOverlayRef.current = overlay;

    return () => {
      overlay.setMap(null);
      overlay.finalize();
      deckOverlayRef.current = null;
    };
  }, [map]);

  // Update heatmap layer when data or settings change
  useEffect(() => {
    if (!deckOverlayRef.current) return;

    // Create heatmap layer if enabled and has data
    if (showHeatmap && heatmapData.length > 0) {
      // Calculate dynamic radius based on zoom
      let radius = 15;
      const minZoom = 4;
      const maxZoom = 20;
      const minRadius = 15;
      const maxRadius = 100;

      if (debouncedZoom >= maxZoom) {
        radius = maxRadius;
      } else if (debouncedZoom <= minZoom) {
        radius = minRadius;
      } else {
        radius =
          minRadius +
          ((maxRadius - minRadius) * (debouncedZoom - minZoom)) /
            (maxZoom - minZoom);
      }

      // Use metric code in layer ID to force re-render when metric changes
      const layerId = selectedHeatmapMetric?.code
        ? `strategy-heatmap-layer-${selectedHeatmapMetric.code}`
        : 'strategy-heatmap-layer-default';

      const heatmapLayer = new HeatmapLayer({
        id: layerId,
        data: heatmapData,
        aggregation: 'SUM',
        getPosition: (d: { position: [number, number] }) => d.position,
        getWeight: (d: { weight: number }) => d.weight,
        radiusPixels: 30 + radius,
        intensity: 0.9,
        threshold: 0.01,
        opacity: 0.6,
        colorRange: [
          [255, 255, 178], // Light yellow
          [254, 204, 92], // Yellow-orange
          [253, 141, 60], // Orange
          [240, 59, 32], // Red-orange
          [189, 0, 38], // Red
          [128, 0, 38], // Dark red
          [103, 0, 113], // Purple
          [63, 0, 125], // Dark purple
        ],
      });

      deckOverlayRef.current.setProps({ layers: [heatmapLayer] });
    } else {
      // Clear layers when heatmap is disabled or no data
      deckOverlayRef.current.setProps({ layers: [] });
    }
  }, [showHeatmap, heatmapData, debouncedZoom, selectedHeatmapMetric?.code]);

  useEffect(() => {
    // Clean up existing polygon if no territory selected
    if (!displayTerritory && polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
      return;
    }

    if (!map || !territory || !displayTerritory) return;

    // Clean up existing polygon
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    const isNew = !!territory?.polygons;
    const territoryPolygons = isNew ? territory.polygons : territory.old_polygons;

    if (!territoryPolygons || territoryPolygons.length === 0) return;

    // Convert territory polygons to Google Maps format (supports holes)
    const polygonPaths = convertTerritoryPolygonsToMapPaths(territoryPolygons);

    // Create Google Maps Polygon
    const polygon = new google.maps.Polygon({
      paths: polygonPaths,
      strokeColor: '#a3a3a3', // neutral-400
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#525252', // neutral-600
      fillOpacity: 0.15,
      editable: false,
      draggable: false,
    });

    polygon.setMap(map);
    polygonRef.current = polygon;

    // Fit bounds to territory (only need boundary coords, not holes)
    const bounds = new google.maps.LatLngBounds();
    territoryPolygons.forEach((polygon) => {
      const boundaryCoords = getPolygonBoundaryCoords(polygon);
      boundaryCoords.forEach((point) => {
        if (
          point &&
          typeof point.lat === 'number' &&
          typeof point.lng === 'number'
        ) {
          bounds.extend(new google.maps.LatLng(point.lat, point.lng));
        }
      });
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
      map.setCenter(bounds.getCenter());
    }

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [map, territory, displayTerritory]);

  return null;
};

export default StrategyMapContent;
